/**
 * Configurable HTTP client factory built on axios.
 *
 * Creates pre-configured axios instances with standard defaults
 * (timeout, headers, error normalization). Each app creates its
 * own fetcher via createFetcher() with app-specific baseURL and
 * optional interceptors.
 */

import type { ApiResponse } from "@plerity/contracts/api";
import { newTraceContext, parseTraceparent, scrubUrl, shouldPropagateTrace } from "@plerity/observability";
import { captureError } from "@plerity/runtime-reporter";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  ApiPathError,
  applyTrailingSlashPolicy,
  assertNoDoubledApiPrefix,
  isApiPathError,
  type TrailingSlashPolicy,
} from "./path-normalization";
export type { ApiResponse };
export { ApiPathError, isApiPathError, type TrailingSlashPolicy };
export type { PaginatedResponse } from "@plerity/contracts/api";

/**
 * Backend-facing paginated response shape (snake_case).
 * The backend sends snake_case pagination fields. Until a transform layer
 * is added to the fetcher, consumers receive this shape at runtime.
 * Use this type when working with raw backend responses.
 */
export interface BackendPaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  /** HTTP status when the error originated from a response (undefined for network errors). */
  status?: number;
  /**
   * Original response data — preserved verbatim so consumers that need to
   * introspect non-Plerity-envelope error bodies (e.g. FastAPI's
   * ``{"detail": {...}}`` HTTPException shape) can do so after normalization.
   * Added 2026-05-10 (Glass canary follow-up): the TriggerOnboardingCard
   * conflict path relies on extracting ``data.detail.prior`` from a 409 body.
   * Pre-2026-05-10, normalizeError discarded ``response.data`` and the
   * card's onError handler couldn't find the prior trigger info, falling
   * through to a generic "An unexpected error occurred" message.
   */
  responseData?: unknown;
}

/**
 * Concrete `Error` subclass carrying the {@link ApiError} shape. `normalizeError`
 * rejects every non-auth failure (HTTP 4xx/5xx, timeout, network) with one of
 * these.
 *
 * Before 2026-10-11 those failures were rejected as plain `ApiError` object
 * literals, so the fleet-wide idiom
 * `err instanceof Error ? err.message : "Unknown error"` always fell through to
 * the fallback and discarded the backend's message (AI SPA LiveBuildsList /
 * BuildHistoryTab rendered "Unknown error" instead of the backend message).
 *
 * `ApiError` stays an exported INTERFACE so existing structural consumers
 * (object-literal fixtures, `err.status` / `err.code` reads) keep type-checking;
 * this class `implements` it. `message` is redefined as an own ENUMERABLE
 * property (a native Error's `message` is non-enumerable). Without that,
 * `{...err}`, `JSON.stringify(err)` and `toEqual({code, message, ...})`
 * consumers would silently lose the message.
 */
// Instance fields are declared via interface/class declaration MERGING (below)
// rather than `declare` class fields: no class-field initializers are emitted, so
// the instance's own enumerable keys are exactly the ones the pre-2026-10-11
// object literal had — AND it stays parseable by the mobile babel chain
// (babel-preset-expo's jest transform runs flow-strip-types, which rejects the
// `declare` field modifier; that broke 17 ops-mobile jest suites on 2026-10-11).
export interface ApiRequestError {
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly status?: number;
  readonly responseData?: unknown;
}

// biome-ignore lint/suspicious/noUnsafeDeclarationMerging: intentional — fields typed via the merged interface above (no emitted initializers)
export class ApiRequestError extends Error implements ApiError {
  constructor(init: ApiError) {
    super(init.message);
    Object.setPrototypeOf(this, ApiRequestError.prototype);
    // Non-enumerable, like a native Error's name, so it does not show up as an extra key.
    Object.defineProperty(this, "name", { value: "ApiRequestError", writable: true, configurable: true });
    Object.defineProperty(this, "message", {
      value: init.message,
      enumerable: true,
      writable: true,
      configurable: true,
    });
    const fields: Record<string, unknown> = { code: init.code };
    if ("details" in init) fields.details = init.details;
    if ("status" in init) fields.status = init.status;
    if ("responseData" in init) fields.responseData = init.responseData;
    Object.assign(this, fields);
  }
}

/** Type guard: is this value an ApiRequestError instance? */
export function isApiRequestError(err: unknown): err is ApiRequestError {
  return err instanceof ApiRequestError;
}

/**
 * Auth-failure error class. Thrown by the fetcher (HTTP 401/403) and by
 * `useApiQuery`/`useApiMutation` (envelope `success:false` with an auth
 * `error_code`) so that React Query surfaces auth failures via `isError = true`
 * and consumers can branch via `instanceof AuthError`.
 *
 * Without this, a CF Access JWT expiry produces a 401 envelope that is
 * silently rendered as a skeleton/empty state — the failure mode the
 * 2026-05-04 AI dashboard incident exposed.
 */
export class AuthError extends Error {
  readonly name = "AuthError";
  /** Backend error_code (AUTH_REQUIRED, AUTH_EXPIRED, FORBIDDEN, ...) or HTTP_401/HTTP_403. */
  readonly code: string;
  /** HTTP status (401 or 403). */
  readonly status: number;
  /**
   * Original response body — preserved verbatim, mirroring the same field on
   * the general `ApiError` shape (added 2026-05-10 for FastAPI
   * ``{"detail": {...}}`` introspection). AuthError previously discarded this,
   * which was fine while no consumer needed anything beyond "you're not
   * authenticated" — until a caller needed to distinguish DIFFERENT 403
   * reasons on the same endpoint (e.g. a WebAuthn step-up gate's
   * `secondary_auth_failed`, which is retryable after a ceremony, vs an
   * `operator_forbidden` 403, which is not).
   */
  readonly responseData?: unknown;

  constructor(message: string, code: string, status: number, responseData?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.responseData = responseData;
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

/** Error codes the backend emits that map to an auth failure. */
export const AUTH_ERROR_CODES = new Set(["AUTH_REQUIRED", "AUTH_EXPIRED", "FORBIDDEN"]);

/** Type guard: is this value an AuthError instance? */
export function isAuthError(err: unknown): err is AuthError {
  return err instanceof AuthError;
}

export interface FetcherConfig {
  /** Base URL for all requests (e.g. "/api" or "https://api.example.com") */
  baseURL?: string;
  /** Request timeout in milliseconds (default: 30000) */
  timeout?: number;
  /** Additional default headers */
  headers?: Record<string, string>;
  /** Send cookies cross-origin (needed for CF Access JWT passthrough) */
  withCredentials?: boolean;
  /** Request interceptor — runs before each request */
  onRequest?: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
  /** Response error interceptor — runs on error responses */
  onResponseError?: (error: AxiosError<ApiError>) => unknown;
  /**
   * HTTP error statuses to treat as EXPECTED and NOT report to observability
   * (`captureError`). Suppressed IN ADDITION to the always-on 401 default (see
   * {@link DEFAULT_EXPECTED_STATUSES}) — e.g. an endpoint whose 404 is a normal
   * "not found". Every other 4xx/5xx is still reported fleet-wide.
   */
  expectedStatuses?: number[];
  /**
   * Attach W3C `traceparent` + `X-Request-ID` headers so a failed request can be
   * joined to its backend trace / log line. Default: `true`. Set `false` to opt out.
   *
   * Headers are added ONLY to same-origin / relative requests and to hosts listed
   * in {@link traceHosts} — never to third parties. A header the caller already
   * set on the request is left untouched.
   *
   * Backend note: for CROSS-origin API hosts (e.g. `crm-api.animasmarketing.com`
   * called from an app origin) the server's CORS `Access-Control-Allow-Headers`
   * must list BOTH `traceparent` and `X-Request-ID`, otherwise the browser's
   * preflight fails and every request to that host breaks. Add the host to
   * {@link traceHosts} only once its CORS allows them.
   */
  traceHeaders?: boolean;
  /**
   * Extra API hosts (`https://crm-api.animasmarketing.com` or a bare host) that
   * should receive trace headers although they are cross-origin. Default: none.
   */
  traceHosts?: string[];
  /**
   * The server's trailing-slash policy for the PATH of every request. Default:
   * `"preserve"` (send the path exactly as written).
   *
   * Why `"preserve"`: every app in the fleet already spells the slash its route
   * declares, and contract tests pin that. Any other default would silently rewrite
   * working URLs. Set `"add"` when the backend declares routes with a trailing
   * slash and `"remove"` when it declares them without, so a call site can no
   * longer cause the FastAPI `redirect_slashes` 307 that the browser then blocks
   * on CORS (Error KB: data app `useJobs`, 2026-10-19). Query strings and
   * fragments are never touched. See {@link TrailingSlashPolicy}.
   */
  trailingSlash?: TrailingSlashPolicy;
  /**
   * Reject (with a typed {@link ApiPathError}) a request whose `baseURL` already
   * contains an `api` path segment while the request path also starts with `/api`,
   * i.e. `/api/api/...`. Default: `true`. The combination can only 404, so
   * throwing at the call site with a precise message beats a 404 that renders as
   * an empty page. Set `false` only for a backend that genuinely mounts
   * `/api/api`.
   */
  refuseDoubledApiPrefix?: boolean;
}

const DEFAULT_TIMEOUT = 30_000;
const TRACEPARENT_HEADER = "traceparent";
const REQUEST_ID_HEADER = "X-Request-ID";

const DEFAULT_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
};

/**
 * Statuses never reported to observability, regardless of app config.
 *
 * A 401 is structurally an auth-expiry / not-authenticated signal, not a fleet
 * API defect: the SPA promotes it to {@link AuthError} and redirects to login,
 * so when a JWT expires every in-flight query 401s at once. Reporting each to
 * `captureError` manufactured a burst of duplicate HIGH-severity Error-KB rows
 * from one ordinary session timeout — Error KB 3ec52085: 18+ duplicate rows
 * from a single browser session (29 beacons inside 195 ms). Always suppressed;
 * apps add further expected statuses via {@link FetcherConfig.expectedStatuses}.
 */
const DEFAULT_EXPECTED_STATUSES: readonly number[] = [401];

/**
 * Create a configured axios instance with standard defaults.
 *
 * @example
 * ```ts
 * const api = createFetcher({ baseURL: '/api', timeout: 15_000 });
 * const { data } = await api.get<ApiResponse<User[]>>('/users');
 * ```
 */
export function createFetcher(config: FetcherConfig = {}): AxiosInstance {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout ?? DEFAULT_TIMEOUT,
    headers: { ...DEFAULT_HEADERS, ...config.headers },
    withCredentials: config.withCredentials ?? false,
  });

  // Union of the always-on defaults (401 auth-expiry) + any app-declared
  // expected statuses. Frozen once per fetcher so the interceptor closure below
  // reuses it on every response.
  const expectedStatuses = new Set<number>([...DEFAULT_EXPECTED_STATUSES, ...(config.expectedStatuses ?? [])]);

  // Registered FIRST so it runs LAST (axios runs request interceptors LIFO):
  // it sees the final body + headers after any app-supplied onRequest hook.
  // The trace-header step lives INSIDE this same built-in interceptor (not a
  // separate one) so it also runs after onRequest and sees the final url /
  // baseURL when deciding whether the target is first-party.
  const traceHosts = config.traceHeaders === false ? null : (config.traceHosts ?? []);
  const trailingSlash = config.trailingSlash ?? "preserve";
  const refuseDoubledApiPrefix = config.refuseDoubledApiPrefix ?? true;
  instance.interceptors.request.use((request) => {
    // Path guards run on the FINAL url/baseURL (after any app onRequest hook and
    // any per-request baseURL override), before anything is sent.
    if (refuseDoubledApiPrefix) assertNoDoubledApiPrefix(request.baseURL, request.url);
    if (trailingSlash !== "preserve" && typeof request.url === "string") {
      request.url = applyTrailingSlashPolicy(request.url, trailingSlash);
    }
    const normalized = dropJsonContentTypeForBinaryBodies(request);
    return traceHosts ? attachTraceHeaders(normalized, traceHosts) : normalized;
  });

  if (config.onRequest) {
    const handler = config.onRequest;
    instance.interceptors.request.use(handler);
  }

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ApiError>) => {
      // A request-path guard rejection is a programmer error raised by the request
      // interceptor above (it has no response). Pass it through untouched:
      // normalizeError would otherwise flatten it into "Network error".
      if (isApiPathError(error)) return Promise.reject(error);
      // Report caught non-2xx responses to observability BEFORE branching to
      // app-specific handling or normalization. This single shared hook lights
      // up the whole SPA fleet for *caught* API errors: React Query swallows
      // these into inline toasts, so @plerity/runtime-reporter's uncaught-only
      // window.error path never sees them (GAP-1).
      // See agents/analysis/api_404_prevention_observability_analysis_2026-07-18.md
      reportFetchError(error, expectedStatuses);
      if (config.onResponseError) {
        return config.onResponseError(error);
      }
      return Promise.reject(normalizeError(error));
    }
  );

  return instance;
}

/**
 * True for request bodies that must NOT be sent as `application/json`:
 * multipart (`FormData`), raw binary (`Blob`/`File`, `ArrayBuffer`, typed
 * arrays) and url-encoded (`URLSearchParams`). Guarded with `typeof` checks so
 * the fetcher stays safe in runtimes that lack a given global.
 */
function isNonJsonBody(data: unknown): boolean {
  if (data == null || typeof data !== "object") return false;
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) return true;
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) return true;
  return false;
}

/**
 * Remove the fetcher's DEFAULT `Content-Type: application/json` when the body
 * is FormData / Blob / URLSearchParams / binary.
 *
 * axios 1.x `transformRequest` JSON-serializes a FormData body
 * (`formDataToJSON`) whenever the content type is JSON, so every multipart
 * upload made through a `createFetcher()` client silently arrived as a JSON
 * object unless the caller remembered `headers: {"Content-Type": undefined}`
 * (~45 call sites; e.g. email contact CSV import). Dropping the header lets
 * axios / the browser pick the correct type (multipart boundary, blob.type,
 * `application/x-www-form-urlencoded`). An explicit non-JSON Content-Type set
 * by the caller is left untouched.
 */
function dropJsonContentTypeForBinaryBodies(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!isNonJsonBody(config.data)) return config;
  const headers = config.headers;
  if (!headers) return config;
  const current = typeof headers.getContentType === "function" ? headers.getContentType() : headers["Content-Type"];
  if (typeof current === "string" && current.toLowerCase().startsWith("application/json")) {
    if (typeof headers.delete === "function") {
      headers.delete("Content-Type");
    } else {
      delete (headers as Record<string, unknown>)["Content-Type"];
    }
  }
  return config;
}

/** Read a header from a plain object or an `AxiosHeaders` instance, case-insensitively. */
function readHeader(headers: unknown, name: string): string | undefined {
  if (headers == null || typeof headers !== "object") return undefined;
  const getter = (headers as { get?: (key: string) => unknown }).get;
  if (typeof getter === "function") {
    const value = getter.call(headers, name);
    return typeof value === "string" && value ? value : undefined;
  }
  const record = headers as Record<string, unknown>;
  const lower = name.toLowerCase();
  for (const key of Object.keys(record)) {
    if (key.toLowerCase() === lower && typeof record[key] === "string" && record[key]) {
      return record[key] as string;
    }
  }
  return undefined;
}

/**
 * Add `traceparent` + `X-Request-ID` to first-party requests (see
 * {@link FetcherConfig.traceHeaders}). The request id reuses the 32-hex trace id,
 * so one value finds the trace (Tempo) and the log lines (Loki).
 */
function attachTraceHeaders(request: InternalAxiosRequestConfig, traceHosts: string[]): InternalAxiosRequestConfig {
  try {
    if (!request.headers) return request;
    if (!shouldPropagateTrace(request.url, request.baseURL, { allowedHosts: traceHosts })) return request;
    const context = newTraceContext();
    if (!readHeader(request.headers, TRACEPARENT_HEADER)) {
      request.headers.set(TRACEPARENT_HEADER, context.traceparent);
    }
    if (!readHeader(request.headers, REQUEST_ID_HEADER)) {
      request.headers.set(REQUEST_ID_HEADER, context.traceId);
    }
  } catch {
    // Tracing must never break a request.
  }
  return request;
}

/**
 * Resolve the backend error_code for observability metadata, preferring the
 * Plerity envelope shape, then the legacy `{code}` shape, then HTTP status.
 * Mirrors the precedence in {@link normalizeError} without allocating a full
 * normalized error (the interceptor may hand off to a custom onResponseError).
 */
function deriveErrorCode(data: unknown, status: number): string {
  if (data != null && typeof data === "object") {
    const env = data as PlerityErrorEnvelope;
    if (env.error_code) return env.error_code;
    if (env.code) return env.code;
  }
  return `HTTP_${status}`;
}

/**
 * Emit a `category: 'network'` runtime-error report for a caught non-2xx API
 * response. Fires for every response with a 4xx/5xx status (business-logic
 * 404s, auth 401/403, server 5xx). Pure network/timeout failures (no
 * response object) are intentionally skipped — they reflect the user's
 * connectivity, not a fleet API signal, and would be high-volume noise.
 *
 * captureError is a no-op when the reporter is uninitialized (SSR, tests,
 * apps that never call initRuntimeReporter), so this is safe fleet-wide. The
 * whole call is defensively wrapped: telemetry must never break a request.
 *
 * `expectedStatuses` (401 auth-expiry always, plus any app-declared) are NOT a
 * fleet API defect and are skipped, so a routine JWT expiry — which 401s every
 * in-flight query at once — does not inflate the Error KB with duplicate
 * HIGH-severity rows (Error KB 3ec52085).
 */
function reportFetchError(error: AxiosError<ApiError>, expectedStatuses: ReadonlySet<number>): void {
  const status = error.response?.status;
  if (typeof status !== "number" || status < 400) return;
  if (expectedStatuses.has(status)) return;
  try {
    const method = (error.config?.method ?? "get").toUpperCase();
    // Query string dropped and token/id-looking path segments templated: the
    // endpoint is reported verbatim in the message and metadata, and an API
    // path can carry a capability token (e.g. a patient intake link).
    const endpoint = scrubUrl(error.config?.url) || "(unknown)";
    const errorCode = deriveErrorCode(error.response?.data, status);
    const traceId = parseTraceparent(readHeader(error.config?.headers, TRACEPARENT_HEADER))?.traceId;
    const requestId =
      readHeader(error.response?.headers, REQUEST_ID_HEADER) ?? readHeader(error.config?.headers, REQUEST_ID_HEADER);
    void captureError(`API ${status} ${method} ${endpoint}`, undefined, "error", {
      category: "network",
      metadata: {
        endpoint,
        method,
        status,
        error_code: errorCode,
        ...(traceId ? { trace_id: traceId } : {}),
        ...(requestId ? { request_id: requestId } : {}),
      },
    });
  } catch {
    // Never let observability reporting break the request pipeline.
  }
}

/**
 * Plerity standard error envelope shape (from `_shared/error_middleware.py`):
 *   { success: false, data: null, error: "<message>", error_code: "<CODE>" }
 *
 * Older / generic shapes also tolerated: { code, message, details }.
 */
interface PlerityErrorEnvelope {
  success?: boolean;
  error?: string;
  error_code?: string;
  // Legacy shape — kept for non-Plerity backends and pre-error-middleware paths.
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

function isPlerityErrorEnvelope(value: unknown): value is PlerityErrorEnvelope {
  return value != null && typeof value === "object";
}

function normalizeError(error: AxiosError<ApiError>): ApiRequestError | AuthError {
  const status = error.response?.status;
  const data = error.response?.data as PlerityErrorEnvelope | undefined;

  // Resolve code + message preferring the Plerity envelope shape, then
  // falling back to the legacy {code, message} shape, then HTTP status.
  let code: string;
  let message: string;
  let details: Record<string, unknown> | undefined;

  if (isPlerityErrorEnvelope(data)) {
    code = data.error_code ?? data.code ?? (status ? `HTTP_${status}` : "UNKNOWN");
    message = data.error ?? data.message ?? error.message;
    details = data.details;
  } else if (status) {
    code = `HTTP_${status}`;
    message = error.message;
  } else if (error.code === "ECONNABORTED") {
    return new ApiRequestError({ code: "TIMEOUT", message: "Request timed out" });
  } else {
    return new ApiRequestError({
      code: "NETWORK_ERROR",
      message: "Network error — check your connection",
    });
  }

  // Promote auth failures to a typed AuthError so React Query consumers can
  // branch via `instanceof AuthError` and a global handler can show a banner.
  if (status === 401 || status === 403 || AUTH_ERROR_CODES.has(code)) {
    return new AuthError(message, code, status ?? 401, data);
  }

  // Preserve the raw response body so card-level onError handlers can drill
  // into non-Plerity-envelope shapes (FastAPI HTTPException ``{"detail": {...}}``,
  // axios-passthrough proxy responses, etc.). Without this, the 409-conflict
  // payload (``detail.prior.event_id``, ``detail.prior.triggered_at``) was lost
  // at normalization and the conflict card rendered the generic fallback.
  // Rejected as a real Error (ApiRequestError) so `err instanceof Error ?
  // err.message : …` consumers surface the backend message, not a fallback.
  return new ApiRequestError({ code, message, details, status, responseData: data });
}

/** Default fetcher with no baseURL — useful for relative paths via Vite proxy */
export const defaultFetcher = createFetcher();

export type { AxiosInstance, AxiosRequestConfig };
